import { useEffect, useState, useRef } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckIcon, ShieldCheck, Loader2, CheckCircle } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { AuthLayout } from "@/components/auth/AuthLayout";
import stripeBlackLogo from "@/assets/stripe_black.svg";
import cardTypes from "@/assets/card_types.png";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { auth, createDirectSubscription } from "@/data/firebase";
import { useAuth } from "@/contexts/AuthContext";
import { signOut } from "firebase/auth";
import { useUser } from "../../contexts/UserContext";
import { trackPixelEvent } from "../../lib/hyros";

const features = [
  "Unlimited reply generations",
  "Extract texts from screenshots",
  "Analyze every chat",
  "Realtime personalized coaching",
  "Battle-tested replies",
  "Built by real dating coaches enhanced by AI",
  "Mobile and desktop app",
];

export function PaymentStep() {
  const navigate = useNavigate();
  const { currentUser, access, refreshCustomClaims } = useAuth();
  const { userData } = useUser();
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [displaySuccess, setDisplaySuccess] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const paymentInitiatedRef = useRef(false);

  useEffect(() => {
    if (access.hasAccess) {
      navigate("/");
    }
  }, [access.hasAccess, navigate]);

  useEffect(() => {
    // Only run this effect if payment has been initiated
    if (!paymentInitiatedRef.current || !isSuccess || !userData) return;

    // If payment failed or requires action that wasn't completed
    if (
      userData.paymentStatus === "payment_failed" ||
      userData.paymentStatus === "requires_action" || // If action was required but not completed, or failed.
      userData.paymentStatus === "incomplete" || // General incompletion
      userData.paymentStatus === "past_due"
    ) {
      setError(
        userData.paymentError ||
          "Your payment could not be processed. Please check your card details or try a different card."
      );
      setIsProcessing(false);
      setIsSuccess(false);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      paymentInitiatedRef.current = false;
      return;
    }

    // If subscription is active (it won't be trialing in this new flow)
    if (userData.subscriptionStatus === "active" && userData.isActive) {
      setDisplaySuccess(true);

      setTimeout(async () => {
        await refreshCustomClaims(); // Refresh the token to get updated claims

        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        navigate("/"); // Navigate to dashboard
        paymentInitiatedRef.current = false;
      }, 2000);
    } else if (userData.paymentStatus === "processing") {
      // Still processing, wait for another update or timeout
      // The timeout in handleSubmit will eventually catch this if it gets stuck
      return;
    }

    // Consider other statuses if necessary, or if they should also clear the timeout and reset.
    // For now, only explicit failure or success will fully reset.
  }, [userData, isSuccess, navigate, refreshCustomClaims]);

  // Redirect to login if not authenticated
  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const form = e.target as HTMLFormElement;
    const nameInput = form.querySelector("#name") as HTMLInputElement;
    const name = nameInput.value;

    if (!name || !name.trim()) {
      setError("Please enter your name");
      return;
    }

    if (!cardComplete) {
      setError("Please complete card details");
      return;
    }

    if (cardError) {
      setError(cardError);
      return;
    }

    const cardElement = elements?.getElement(CardElement);
    if (!cardElement) {
      setError(
        "Card element not found. Please refresh the page and try again."
      );
      return;
    }

    setIsProcessing(true);
    setError(null);

    if (!stripe || !elements || !currentUser?.email) {
      setError(
        "Stripe or user details are not available. Please refresh and try again."
      );
      setIsProcessing(false);
      return;
    }

    try {
      // 1. Create PaymentMethod
      const { error: pmError, paymentMethod } =
        await stripe.createPaymentMethod({
          type: "card",
          card: cardElement,
          billing_details: {
            name: name,
            email: currentUser.email,
          },
        });

      if (pmError || !paymentMethod) {
        throw new Error(pmError?.message || "Failed to create payment method.");
      }

      // 2. Call createDirectSubscription
      const directSubResult = await createDirectSubscription({
        paymentMethodId: paymentMethod.id,
        name: name,
      });

      // Type assertion for the result data
      const resultData = directSubResult.data as {
        success: boolean;
        requiresAction?: boolean;
        paymentIntentClientSecret?: string;
        subscriptionId?: string;
        subscriptionStatus?: string;
        message?: string;
        paymentStatus?: string; // User-facing status from backend
      };

      if (resultData.requiresAction && resultData.paymentIntentClientSecret) {
        // 3. Handle 3D Secure or other actions
        const { error: confirmError, paymentIntent } =
          await stripe.confirmCardPayment(resultData.paymentIntentClientSecret);

        if (confirmError) {
          throw new Error(
            confirmError.message || "Payment confirmation failed."
          );
        }

        if (paymentIntent?.status === "succeeded") {
          // Payment successful after action
          setIsSuccess(true);
          trackPixelEvent("Purchase", { currency: "USD", value: 19.0 });
          paymentInitiatedRef.current = true; // Start polling/monitoring via useEffect
        } else {
          // Payment not successful after action
          throw new Error(
            `Payment not successful after confirmation: ${paymentIntent?.status}`
          );
        }
      } else if (resultData.success) {
        // Subscription successful without further action
        setIsSuccess(true);
        trackPixelEvent("Purchase", { currency: "USD", value: 19.0 });
        paymentInitiatedRef.current = true; // Start polling/monitoring via useEffect
      } else {
        // Direct subscription failed or is not immediately active
        throw new Error(
          resultData.message ||
            `Subscription creation failed. Status: ${resultData.subscriptionStatus}, Payment: ${resultData.paymentStatus}`
        );
      }

      // Set a timeout to handle the case where the webhook/Firestore update is delayed
      timeoutRef.current = setTimeout(() => {
        if (!isSuccess) {
          // Only if we haven't already navigated due to success
          setError(
            "Subscription status is taking longer than expected to update. Please refresh the page to check your status or contact support if the issue persists."
          );
          setIsProcessing(false);
          paymentInitiatedRef.current = false;
        }
      }, 30000); // 30 seconds timeout
    } catch (error) {
      console.error("Subscription error:", error);

      // The server refused because this user already has a live subscription
      // and has re-synced their claims. No charge was made; just let them in.
      const code = (error as { details?: { code?: string } })?.details?.code;
      if (code === "subscription_active") {
        await refreshCustomClaims();
        navigate("/");
        return;
      }

      setError(
        error instanceof Error
          ? error.message
          : "An error occurred during subscription setup."
      );
      setIsProcessing(false);
      setIsSuccess(false);
      paymentInitiatedRef.current = false; // Reset on error
    }
  };

  if (displaySuccess) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4">
        <CheckCircle className="w-24 h-24 text-green-500 mb-6" />
        {/* <h1 className="text-3xl font-bold mb-4">Free Trial Activated</h1> */}
        <h1 className="text-3xl font-bold mb-4">Payment Successful</h1>

        <p className="text-gray-600">
          You will be redirected to the app shortly.
        </p>
      </div>
    );
  }

  return (
    <AuthLayout>
      <Card className="w-full max-w-3xl">
        <CardHeader className="text-left">
          {/* <CardTitle className="text-xl">Start Your 7 Day Free Trial</CardTitle> */}
          <CardTitle className="text-xl">
            Options AI Premium (50% off)
          </CardTitle>
          <CardDescription className="text-gray-700">
            {/* Get access to all features for $19 / month */}
            $19 /month. Cancel anytime.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
          <div className="grid gap-6">
            <div className="grid gap-4">
              <form
                onSubmit={handleSubmit}
                className="space-y-0 sm:space-y-0 flex flex-col gap-8 sm:flex-row"
              >
                <div className="space-y-4 flex-1">
                  <div className="grid gap-4">
                    <div className="grid gap-2 relative">
                      <Label htmlFor="card">Email</Label>
                      <Input
                        id="email"
                        placeholder="John Doe"
                        disabled
                        className="bg-gray-200"
                        value={currentUser.email || ""}
                      />
                      <div className="absolute right-2 top-6">
                        <button
                          className="h-8 font-medium hover:text-gray-500 text-xs text-gray-500"
                          disabled={isProcessing}
                          onClick={() => {
                            signOut(auth);
                            navigate("/login");
                          }}
                        >
                          Sign Out
                        </button>
                      </div>
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="name">Name</Label>
                      <Input
                        id="name"
                        placeholder="John Doe"
                        className="placeholder:text-gray-400"
                      />
                    </div>
                    <div className="grid gap-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="card">Card details</Label>
                        <img src={cardTypes} alt="Card types" className="h-6" />
                      </div>
                      <div className="w-full rounded-md border border-input p-2.5 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:border-gray-400 focus-visible:outline-none min-h-10">
                        <CardElement
                          options={{
                            disableLink: true,
                            style: {
                              base: {
                                fontWeight: "300",
                                fontFamily:
                                  "system-ui, -apple-system, sans-serif",
                                "::placeholder": {
                                  color: "#9ca3af",
                                },
                              },
                            },
                          }}
                          onChange={(e) => {
                            setCardComplete(e.complete);
                            setCardError(e.error ? e.error.message : null);
                            if (!e.error) {
                              setError(null);
                            }
                          }}
                        />
                      </div>
                    </div>

                    {(error || cardError) && (
                      <div className="rounded-md bg-destructive/15 p-3 text-sm text-destructive">
                        {error || cardError}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-4 justify-between pt-2">
                    <div className="flex items-center gap-2">
                      <ShieldCheck
                        className="h-7 w-7 text-white flex-shrink-0 rounded-full p-1 bg-green-100"
                        strokeWidth={1.5}
                        fill={"#00A53D"}
                      />
                      <p className="text-xs text-black font-medium">
                        Secure checkout
                      </p>
                    </div>
                    <img src={stripeBlackLogo} alt="Stripe" className="h-6" />
                  </div>
                  <div className="space-y-2">
                    <Button
                      type="submit"
                      className="w-full bg-purple-600 text-white hover:bg-purple-700"
                      disabled={isProcessing}
                      variant="custom"
                    >
                      {isProcessing ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Please wait
                        </>
                      ) : (
                        // "Start Free Trial"
                        // "Continue"
                        "Pay Now"
                      )}
                    </Button>
                    {/* <p className="text-xs text-center text-gray-500">
                      Your free trial will start immediately. After 7 days you
                      will be billed $19 monthly.
                    </p> */}
                    <p className="text-xs text-center text-gray-500">
                      {/* Cancel anytime with one click. You will be billed $19
                      monthly. */}
                      {/*You will be billed $19 /month. Cancel anytime.*/}
                      14-day money back guarantee.
                    </p>
                  </div>
                </div>

                <div className="flex-1 border sm:border-[0px] rounded-md p-4">
                  <h2 className="text-base font-medium mb-4">
                    Get instant access to the #1 Wingman App built by Dating
                    Coaches.
                  </h2>
                  <h3 className="text-lg font-semibold">What's included</h3>
                  <ul className="grid gap-3 mt-2">
                    {features.map((feature) => (
                      <li key={feature} className="flex items-center gap-2">
                        <CheckIcon className="h-4 w-4 text-purple-600 flex-shrink-0" />
                        <span className="text-sm text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </form>
            </div>
          </div>
        </CardContent>
      </Card>
      {/* <div className="text-left text-sm text-gray-400 ">
        <button
          className=" font-normal hover:underline text-sm text-gray-400 "
          onClick={() => {
            signOut(auth);
            navigate("/login");
          }}
        >
          Cancel
        </button>
      </div> */}
      <div className="h-20"></div>
    </AuthLayout>
  );
}
